import React, { useEffect, useState } from "react";
import {
  LoadingPageWrapper,
  StandardPageWrapper,
} from "../../../components/layout/StandardPageWrapper";
import { useOrganizationVendors, usePrimaryOrganization } from "../../../hooks";
import {
  GDPRProcessorRecommendation,
  GdprSccVendorReviewStatus,
  GppSignalStatus,
  OrganizationDataRecipientDto,
  ProcessingRegion,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import _ from "lodash";
import clsx from "clsx";
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import {
  Button,
  Breadcrumbs,
  ExplanatoryCard,
  Slideout,
  TextInput,
  ToolTip,
  SimpleCheckbox,
} from "@truevault/aquilo";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowsUpDownIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { UpdateVendorArgs } from "../components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import { Api } from "../../../../common/service/Api";
import { useActionRequest } from "../../../../common/hooks/api";
import { Form } from "react-final-form";
import { VendorRadioSection } from "../../../components/get-compliant/vendors/VendorFlagWithDocumentation";
import { isThirdPartyRecipient } from "../../../util/vendors";
import { SurveyAnswers, useSurvey } from "../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../get-compliant/data-recipients/shared";
import {
  calculateDisclosureMeta,
  CLASSIFICATION_OPTIONS,
  ClassificationType,
  initialStateFrom,
  isGDPRControllerDocumentationOptional,
  isThirdPartyDocumentationOptional,
  GdprProcessorLanguage,
  ServiceProviderLanguage,
  THIRD_PARTY_CLASSIFICATION_OPTION,
} from "../../../components/organisms/data-recipients/DataRecipientDisclosureSettings";
import {
  saveCaContractRequirementDocumentation,
  saveGdprDocumentation,
  saveGppSignalDocumentation,
  saveSccDocumentation,
  saveServiceProviderDocumentation,
} from "./saveFiles";
import {
  getClassificationIdForClassificationType,
  getVendorClassificationSlug,
  internationalDataTransferOptionsForVendor,
  isVendorServiceClassificationLockedToThirdParty,
} from "./vendorDocumentationUtils";
import { VendorBooleanQuestion } from "../../../components/vendor/VendorBooleanQuestion";
import { PROCESSOR_CLASSIFICATION_OPTIONS } from "../../../components/get-compliant/vendors/GDPRProcessorLanguageLocator";
import { Routes } from "../../../root/routes";
import {
  getCaContractRequirementCell,
  getGdprClassificationCell,
  getGppSignalCell,
  getInternationalDataTransferCellText,
  getVendorClassificationCell,
  VendorTableCellPillDisplay,
} from "./vendorTableCellDisplay";
import { ProcessingRegionToFlagIcon, VendorRegionCheckbox } from "./vendorDocumentationEditorUi";
import { EmailVendorDialog } from "./emailVendorDialog";
import validator from "validator";
import { CircularProgress } from "@mui/material";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";
import { useHistory } from "react-router";
import { Iso8601Date } from "../../../../common/service/server/Types";
import { CheckCircleIcon, EnvelopeIcon } from "@heroicons/react/24/solid";
import { Fmt } from "../../../formatters";
import { SafeCountriesDialog } from "../../../components/dialog/SafeCountriesDialog";

export const doesVendorHaveUsClassification = (vendor: OrganizationDataRecipientDto): boolean => {
  const classificationSlug = getVendorClassificationSlug(vendor);

  if (!classificationSlug || classificationSlug === "needs-review") {
    return false;
  }

  const isThirdParty =
    classificationSlug === "third-party" || isVendorServiceClassificationLockedToThirdParty(vendor);
  // If third party, documentation is optional
  if (isThirdParty) {
    return true;
  }

  return Boolean(
    // check for our documentation URL from vendor database
    vendor.serviceProviderLanguageUrl ||
      // check for user-provided documentation URL
      vendor.publicTosUrl ||
      // check for user-provided documentation file upload
      (vendor.tosFileName && vendor.tosFileName !== "DELETE_") ||
      // no external documentation required if contract is stored internally
      vendor.contractStoredInternally === true,
  );
};

export const isUsClassificationRequiredForVendor = (
  vendor: OrganizationDataRecipientDto,
  org: OrganizationDto,
): boolean => {
  // Must be in US or Canada
  let isUsRegion = vendor.processingRegions.includes("UNITED_STATES");
  let isCanadaRegion = org.featurePipeda && vendor.processingRegions.includes("CANADA");
  if (!isUsRegion && !isCanadaRegion) {
    return false;
  }

  // Must be consumer vendor
  let isConsumer = vendor?.collectionContext?.includes("CONSUMER");
  if (!isConsumer) {
    return false;
  }

  // If in US, then must not be regulated by other US privacy laws
  if (isUsRegion && vendor.regulatedByOtherUSPrivacyLaws === true) {
    return false;
  }

  return true;
};

// May still return true or false even if `vendor` is not in the EEA/UK region, so caller is responsible for checking GDPR status for both org and vendor
export const isVendorGdprClassificationComplete = (
  vendor: OrganizationDataRecipientDto,
): boolean => {
  if (vendor.gdprProcessorSetting === "Processor") {
    return Boolean(
      vendor.gdprProcessorGuaranteeUrl ||
        vendor.gdprProcessorGuaranteeFileName ||
        vendor.gdprProcessorLanguageUrl ||
        vendor.gdprContractStoredInternally === true,
    );
  } else if (vendor.gdprProcessorSetting === "Controller") {
    return Boolean(
      vendor.gdprControllerGuaranteeUrl ||
        vendor.gdprControllerGuaranteeFileName ||
        vendor.gdprProcessorLanguageUrl ||
        vendor.gdprContractStoredInternally === true,
    );
  }

  return false;
};

export const isGdprInternationalDataTransferComplete = (
  vendor: OrganizationDataRecipientDto,
): boolean => {
  if (vendor.gdprSccVendorReviewStatus === "DOCS_PROVIDED") {
    return Boolean(vendor.gdprSccFileName || vendor.gdprSccUrl);
  }

  return Boolean(vendor.gdprSccVendorReviewStatus || vendor.gdprHasSccRecommendation);
};

export const doesGdprInternationalDataTransferApplyForVendor = (
  vendor: OrganizationDataRecipientDto,
): boolean => {
  // Use `!== false` instead of `== true` since if the field is empty, we want to default to enabling international data transfers for the vendor
  return vendor.gdprInternationalDataTransferRulesApply !== false;
};

// Note: when updating this, also update `checkConsentManagementDevInstructionsCompletionStatus` in `OrganizationOnboardingProgressService.kt`
export const doesGppSignalApplyForVendor = (vendor: OrganizationDataRecipientDto): boolean => {
  // GPP signal doesn't apply if vendor is in the US and is regulated by other US privacy laws (POL-6562)
  if (
    vendor.processingRegions.includes("UNITED_STATES") &&
    vendor.regulatedByOtherUSPrivacyLaws === true
  ) {
    return false;
  }

  return (
    getVendorClassificationSlug(vendor) === "third-party" &&
    (vendor?.ccpaIsSelling || vendor?.isAlwaysDataSellingVendor || vendor?.ccpaIsSharing)
  );
};

export const isVendorGppSignalDocumentationComplete = (
  vendor: OrganizationDataRecipientDto,
): boolean => {
  if (!vendor.gppSignalStatus) {
    return false;
  }

  // Only require documentation for the GPP_SIGNAL_RESPECTED answer
  if (vendor.gppSignalStatus !== "GPP_SIGNAL_RESPECTED") {
    return true;
  }

  // Since signal is respected, we need to check if the signal document is uploaded
  return Boolean(
    vendor.gppSignalDocumentationUrl ||
      (vendor.gppSignalDocumentationFileName &&
        vendor.gppSignalDocumentationFileName !== "DELETE_"),
  );
};

export const isCaContractFieldRequiredForVendor = (
  vendor: OrganizationDataRecipientDto,
): boolean => {
  return (
    vendor.processingRegions.includes("UNITED_STATES") &&
    vendor.regulatedByOtherUSPrivacyLaws !== true
  );
};

export const isVendorCaContractDocumentationComplete = (
  vendor: OrganizationDataRecipientDto,
): boolean => {
  return Boolean(
    vendor.caContractRequirementUrl ||
      vendor.cpraContractLanguageUrl ||
      (vendor.caContractRequirementFileName && vendor.caContractRequirementFileName !== "DELETE_"),
  );
};

// Determine whether the vendor appears in the Unclassified Vendors or Classified Vendors table
// To be considered classified, a vendor must have a US Classification and a CA Contract requirement documentation
export const isVendorDocumentationClassified = (
  org: OrganizationDto,
  vendor: OrganizationDataRecipientDto,
) => {
  if (isUsClassificationRequiredForVendor(vendor, org) && !doesVendorHaveUsClassification(vendor)) {
    return false;
  }

  if (
    isCaContractFieldRequiredForVendor(vendor) &&
    !isVendorCaContractDocumentationComplete(vendor)
  ) {
    return false;
  }

  if (doesGppSignalApplyForVendor(vendor) && !isVendorGppSignalDocumentationComplete(vendor)) {
    return false;
  }

  // EEA/UK region checks
  let isEeaUk = org?.featureGdpr && vendor.processingRegions.includes("EEA_UK");
  if (isEeaUk) {
    if (!isVendorGdprClassificationComplete(vendor)) {
      return false;
    }
    if (
      doesGdprInternationalDataTransferApplyForVendor(vendor) &&
      !isGdprInternationalDataTransferComplete(vendor)
    ) {
      return false;
    }
  }

  return true;
};

export function VendorDocumentationPage() {
  const history = useHistory();

  const [org, ,] = usePrimaryOrganization();
  const [allVendors, , refreshVendors] = useOrganizationVendors(
    org?.id,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
    /* onlyUseSettled = */ true,
  );

  const [vendorsState, setVendorsState] = useState<OrganizationDataRecipientDto[] | null>(null);

  const [sidebarOpenForVendorId, setSidebarOpenForVendorId] = useState<string | null>(null);

  const { answers: sellingAndSharingAnswers } = useSurvey(org?.id, SELLING_AND_SHARING_SURVEY_NAME);

  // Keep track of the completion state on initial load so we know whether to show the footer with a Done button
  const [isInitiallyCompleted, setIsInitiallyCompleted] = useState(null);

  useEffect(() => {
    if (allVendors && allVendors?.length > 0 && vendorsState === null) {
      setVendorsState(allVendors);

      if (isInitiallyCompleted === null) {
        setIsInitiallyCompleted(allVendors.every((v) => isVendorDocumentationClassified(org, v)));
      }
    }
  }, [allVendors, vendorsState, setVendorsState, isInitiallyCompleted, setIsInitiallyCompleted]);

  const { fetch: updateDataRecipient } = useActionRequest({
    api: async (v: UpdateVendorArgs) => {
      const { vendorId, vendorSettings } = v;
      await Api.organizationVendor.addOrUpdateVendor(org?.id, vendorId, vendorSettings);
    },
    onSuccess: refreshVendors,
  });

  if (vendorsState === null) {
    return <LoadingPageWrapper />;
  }

  const [classifiedVendors, unclassifiedVendors] = _.partition(
    vendorsState || [],
    (e) => isVendorDocumentationClassified(org, e), // returning true from predication lambda will place result in first list (which is classified vendors)
  );

  const updateVendor = async (updateVendorArgs: UpdateVendorArgs) => {
    setVendorsState(
      vendorsState.map((vendor) => {
        if (vendor.vendorId === updateVendorArgs.vendorId) {
          let updatedVendor = { ...vendor, ...updateVendorArgs.vendorSettings };
          // Re-create the classification DTO object for the local state (normally this is done by the API, so to be consistent we apply the same mutation to our local state)
          let newClassification = updatedVendor.classificationOptions.find(
            (s) => s.id === updatedVendor.classificationId,
          );
          if (newClassification) {
            updatedVendor.classification = newClassification;
          }
          return updatedVendor;
        }
        return vendor;
      }),
    );

    // Ensure we convert the classification DTO object to the classification ID (if a new classification ID wasn't provided)
    if (
      !updateVendorArgs?.vendorSettings?.classificationId &&
      "classification" in updateVendorArgs.vendorSettings
    ) {
      updateVendorArgs.vendorSettings.classificationId =
        // @ts-ignore - we know that if the classification property exists, it's a DTO object and must have an ID
        updateVendorArgs.vendorSettings.classification?.id;
    }
    await updateDataRecipient(updateVendorArgs);
  };

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        // Hide the footer if the sidebar is open
        isInitiallyCompleted || sidebarOpenForVendorId ? undefined : (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent={"Done"}
                nextOnClick={() => history.push(Routes.dataMap.dashboard)}
                // For now, always let users move on to the next step since this page has a lot to complete - see https://linear.app/truevault/issue/POL-6401/pol-6272-qa-2025-09-04#comment-d59225bb
                nextDisabled={false}
              />
            }
          />
        )
      }
    >
      <div className="tw-boundary">
        <div className="mb-[32px]">
          <Breadcrumbs
            hideHomeIcon={true}
            title="Vendor Documents"
            source="Data Map"
            sourceRoute={Routes.dataMap.dashboard}
          />
        </div>

        <div className="max-w-[1055px] mb-[24px]">
          <ExplanatoryCard
            topBody={
              <div>
                <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3">
                  Vendor Documentation Requirements
                </h3>
              </div>
            }
            hiddenBody={
              <div className="text-sm font-normal leading-5 text-gray-500 space-y-4">
                <p>
                  The table below lists all the vendors in your Data Map. Each column is a different
                  privacy requirement that can be fulfilled and verified by uploading or linking to
                  their documentation found within each vendor's Privacy Policy, Terms of Service,
                  Data Processing Agreements, or other contracts/documents. The correct
                  documentation can be challenging to find sometimes.
                </p>
                <p>
                  TrueVault has automatically filled in parts of the table below for any vendors in
                  our database with publicly available documentation.
                </p>
                <p>
                  For any "unverified" sections where vendor documentation is not publicly available
                  or easily accessible, you can simply contact the vendor directly from this screen
                  to ask about any of the requirements below. Just click the arrow on the far right
                  of a vendor's row to open the side panel, enter an email address for that vendor
                  and send any of the pre-written templates provided. Replies will come directly to
                  your privacy email, then you can come back and update the table accordingly.
                </p>
                <p>
                  For more information on each requirement in the table, refer to our{" "}
                  <a
                    href="https://knowledge.truevault.com/article/351-privacy-documentation-for-vendors"
                    target="_blank"
                    rel="noreferrer"
                    className="text-gray-500 underline font-medium"
                  >
                    Vendor Documentation Guide
                  </a>{" "}
                  and/or hover over the info icons next to the table headers.
                </p>
              </div>
            }
            expandable={true}
            initialExpanded={true}
          />
        </div>

        {unclassifiedVendors.length > 0 && (
          <div className="mb-[24px]">
            <div className="max-w-[1055px] p-[16px] rounded-[6px] bg-yellow-100 flex items-start self-stretch gap-[12px]">
              <div className="pt-[4px]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.25694 3.09882C9.02154 1.73952 10.9786 1.73952 11.7432 3.09882L17.3235 13.0194C18.0735 14.3526 17.11 15.9999 15.5804 15.9999H4.41978C2.89013 15.9999 1.9267 14.3526 2.67663 13.0194L8.25694 3.09882ZM11 13C11 13.5523 10.5523 14 10 14C9.44772 14 9 13.5523 9 13C9 12.4477 9.44772 12 10 12C10.5523 12 11 12.4477 11 13ZM10 5C9.44772 5 9 5.44772 9 6V9C9 9.55228 9.44772 10 10 10C10.5523 10 11 9.55228 11 9V6C11 5.44772 10.5523 5 10 5Z"
                    fill="#FFC736"
                  />
                </svg>
              </div>
              <div>
                <div className="text-yellow-800 text-[14px] font-medium">Instructions</div>
                <div className="text-yellow-700 text-[14px] font-normal leading-[21px] mt-[12px]">
                  <p>
                    Complete any requirements with a yellow "Unverified" badge by adding their
                    documentation. If you cannot find the documentation or it is not publicly
                    available, click the arrow and contact the vendor directly through the side
                    panel.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {classifiedVendors.length > 0 || unclassifiedVendors.length > 0 ? (
          <VendorDocumentationTable
            org={org}
            classifiedVendors={classifiedVendors}
            unclassifiedVendors={unclassifiedVendors}
            updateVendor={updateVendor}
            sidebarOpenForVendorId={sidebarOpenForVendorId}
            setSidebarOpenForVendorId={setSidebarOpenForVendorId}
            sellingAndSharingAnswers={sellingAndSharingAnswers}
            allVendors={vendorsState}
          />
        ) : (
          <div className="text-neutral-800 text-[14px] italic">No vendors</div>
        )}
      </div>
    </StandardPageWrapper>
  );
}

type VendorTableSortOrder =
  | "name_ascending"
  | "name_descending"
  | "ca_contract_ascending"
  | "ca_contract_descending"
  | "gpp_ascending"
  | "gpp_descending"
  | "us_classification_ascending"
  | "us_classification_descending"
  | "gdpr_classification_ascending"
  | "gdpr_classification_descending";

function VendorDocumentationTable({
  org,
  classifiedVendors,
  unclassifiedVendors,
  updateVendor,
  sidebarOpenForVendorId,
  setSidebarOpenForVendorId,
  sellingAndSharingAnswers,
  allVendors,
}: {
  org: OrganizationDto;
  classifiedVendors: OrganizationDataRecipientDto[];
  unclassifiedVendors: OrganizationDataRecipientDto[];
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  sidebarOpenForVendorId: string | null;
  setSidebarOpenForVendorId: (id: string | null) => void;
  sellingAndSharingAnswers: SurveyAnswers;
  allVendors: OrganizationDataRecipientDto[];
}) {
  const [sortOrder, setSortOrder] = useState<VendorTableSortOrder | null>("name_ascending");

  const tableHeaderStyles = clsx(
    "h-[40px]",
    "bg-neutral-200",
    "border-b border-solid border-gray-200",
    "text-xs font-medium text-gray-500 uppercase",
  );
  const tableHeader = (
    name: string,
    className: string = "",
    tooltipText: string = "",
    onSortClick?: () => void,
  ) => {
    let hasTooltip = tooltipText && tooltipText.length > 0;
    let hasSortIcon = Boolean(onSortClick);

    if (!hasTooltip && !hasSortIcon) {
      return (
        <th scope="col" className={tableHeaderStyles + " " + className}>
          {name}
        </th>
      );
    }

    return (
      <th scope="col" className={tableHeaderStyles + " " + className}>
        <div className="flex items-center justify-center gap-[8px]">
          <span>{name}</span>
          {hasSortIcon && (
            <ArrowsUpDownIcon
              className="w-[16px] h-[16px] text-neutral-700 cursor-pointer"
              onClick={onSortClick}
            />
          )}
          {hasTooltip && (
            <ToolTip text={tooltipText} width="210px" xOffset={-60} yOffset={110}>
              <InformationCircleIcon className="w-[16px] h-[16px] text-blue-gray-900" />
            </ToolTip>
          )}
        </div>
      </th>
    );
  };

  let unclassifiedVendorsSorted = unclassifiedVendors,
    classifiedVendorsSorted = classifiedVendors;
  if (sortOrder) {
    unclassifiedVendorsSorted = unclassifiedVendors.sort((a, b) =>
      vendorComparisonFn(a, b, sortOrder, org),
    );
    classifiedVendorsSorted = classifiedVendors.sort((a, b) =>
      vendorComparisonFn(a, b, sortOrder, org),
    );
  }

  return (
    <div
      className={clsx(
        "border border-solid border-gray-200 rounded-lg max-h-[636px] overflow-y-auto overflow-x-hidden",
        "max-w-[1055px]",
      )}
    >
      <table className="w-full" style={{ tableLayout: "fixed" }}>
        {org.featureGdpr ? (
          <colgroup>
            <col style={{ width: "143px" }} /> {/* Vendor Name */}
            <col style={{ width: "143px" }} /> {/* Regions */}
            <col style={{ width: "143px" }} /> {/* CA Contract */}
            <col style={{ width: "143px" }} /> {/* GPP */}
            <col style={{ width: "143px" }} /> {/* US Class */}
            <col style={{ width: "143px" }} /> {/* GDPR Class */}
            <col style={{ width: "147px" }} /> {/* Intl Data Transfers */}
            <col style={{ width: "50px" }} /> {/* Arrow */}
          </colgroup>
        ) : (
          <colgroup>
            <col style={{ width: "201px" }} /> {/* Vendor Name */}
            <col style={{ width: "201px" }} /> {/* Regions */}
            <col style={{ width: "201px" }} /> {/* CA Contract */}
            <col style={{ width: "201px" }} /> {/* GPP */}
            <col style={{ width: "201px" }} /> {/* US Class */}
            <col style={{ width: "50px" }} /> {/* Arrow */}
          </colgroup>
        )}
        <thead className="sticky top-0 z-10">
          <tr>
            {tableHeader(
              "Vendor Name",
              "rounded-tl-lg pl-6 text-left",
              /* tooltipText = */ null,
              /* onSortClick = */ () => {
                if (sortOrder === "name_ascending") {
                  setSortOrder("name_descending");
                } else {
                  setSortOrder("name_ascending");
                }
              },
            )}
            {tableHeader("Regions")}
            {tableHeader(
              "CA Contract",
              /* className = */ "",
              /* tooltipText = */ "Whether they have agreed to special data protections required by California law.",
              /* onSortClick = */ () => {
                if (sortOrder === "ca_contract_ascending") {
                  setSortOrder("ca_contract_descending");
                } else {
                  setSortOrder("ca_contract_ascending");
                }
              },
            )}
            {tableHeader(
              "GPP",
              /* className = */ "",
              /* tooltipText = */ "Whether they respect Global Privacy Protocol signals for advertising opt-outs and online consent.",
              /* onSortClick = */ () => {
                if (sortOrder === "gpp_ascending") {
                  setSortOrder("gpp_descending");
                } else {
                  setSortOrder("gpp_ascending");
                }
              },
            )}
            {tableHeader(
              "US Class",
              /* className = */ "",
              /* tooltipText = */ "Are they considered a service provider or a third party under U.S. privacy laws.",
              /* onSortClick = */ () => {
                if (sortOrder === "us_classification_ascending") {
                  setSortOrder("us_classification_descending");
                } else {
                  setSortOrder("us_classification_ascending");
                }
              },
            )}

            {org.featureGdpr && (
              <>
                {tableHeader(
                  "GDPR Class",
                  /* className = */ "",
                  /* tooltipText = */ "Are they considered a processor or controller under GDPR.",
                  /* onSortClick = */ () => {
                    if (sortOrder === "gdpr_classification_ascending") {
                      setSortOrder("gdpr_classification_descending");
                    } else {
                      setSortOrder("gdpr_classification_ascending");
                    }
                  },
                )}
                {tableHeader("Intl Data Transfers")}
              </>
            )}

            {tableHeader(" ")}
          </tr>
        </thead>
        <tbody>
          <>
            {unclassifiedVendorsSorted.length > 0 && (
              <tr className="border-gray-200">
                <th
                  scope="colgroup"
                  className="bg-yellow-100 font-medium text-left text-[14px] text-yellow-800 py-[11px] pl-[24px]"
                  colSpan={org.featureGdpr ? 8 : 6}
                >
                  Unverified
                </th>
              </tr>
            )}
            {unclassifiedVendorsSorted.map((vendor) => (
              <VendorDocumentationTableRow
                key={vendor.id}
                org={org}
                vendor={vendor}
                updateVendor={updateVendor}
                isSidebarOpen={sidebarOpenForVendorId === vendor.vendorId}
                openSidebar={() => setSidebarOpenForVendorId(vendor.vendorId)}
                closeSidebar={() => setSidebarOpenForVendorId(null)}
                sellingAndSharingAnswers={sellingAndSharingAnswers}
                allVendors={allVendors}
                navigateNextVendorInSidebar={() => {
                  let idx = unclassifiedVendorsSorted.findIndex((v) => v.id === vendor.id);
                  if (idx == -1) return;
                  let nextIdx = (idx + 1) % unclassifiedVendorsSorted.length;
                  setSidebarOpenForVendorId(unclassifiedVendorsSorted[nextIdx].vendorId);
                }}
                navigatePreviousVendorInSidebar={() => {
                  let idx = unclassifiedVendorsSorted.findIndex((v) => v.id === vendor.id);
                  if (idx == -1) return;
                  let prevIdx =
                    (idx - 1 + unclassifiedVendorsSorted.length) % unclassifiedVendorsSorted.length;
                  setSidebarOpenForVendorId(unclassifiedVendorsSorted[prevIdx].vendorId);
                }}
              />
            ))}

            {classifiedVendorsSorted.length > 0 && (
              <tr className="border-gray-200">
                <th
                  scope="colgroup"
                  className="bg-neutral-0 font-medium text-left text-[14px] text-yellow-800 py-[11px] pl-[24px]"
                  colSpan={org.featureGdpr ? 8 : 6}
                >
                  Verified
                </th>
              </tr>
            )}
            {classifiedVendorsSorted.map((vendor) => (
              <VendorDocumentationTableRow
                key={vendor.id}
                org={org}
                vendor={vendor}
                updateVendor={updateVendor}
                isSidebarOpen={sidebarOpenForVendorId === vendor.vendorId}
                openSidebar={() => setSidebarOpenForVendorId(vendor.vendorId)}
                closeSidebar={() => setSidebarOpenForVendorId(null)}
                sellingAndSharingAnswers={sellingAndSharingAnswers}
                allVendors={allVendors}
                navigateNextVendorInSidebar={() => {
                  let idx = classifiedVendorsSorted.findIndex((v) => v.id === vendor.id);
                  if (idx == -1) return;
                  let nextIdx = (idx + 1) % classifiedVendorsSorted.length;
                  setSidebarOpenForVendorId(classifiedVendorsSorted[nextIdx].vendorId);
                }}
                navigatePreviousVendorInSidebar={() => {
                  let idx = classifiedVendorsSorted.findIndex((v) => v.id === vendor.id);
                  if (idx == -1) return;
                  let prevIdx =
                    (idx - 1 + classifiedVendorsSorted.length) % classifiedVendorsSorted.length;
                  setSidebarOpenForVendorId(classifiedVendorsSorted[prevIdx].vendorId);
                }}
              />
            ))}
          </>
        </tbody>
      </table>
    </div>
  );
}

const vendorComparisonFn = (a, b, sortOrder: VendorTableSortOrder, org: OrganizationDto) => {
  var fieldA = a.name,
    fieldB = b.name;

  if (sortOrder === "ca_contract_ascending" || sortOrder === "ca_contract_descending") {
    fieldA = getCaContractRequirementCell(a).text;
    fieldB = getCaContractRequirementCell(b).text;
  } else if (sortOrder === "gpp_ascending" || sortOrder === "gpp_descending") {
    fieldA = getVendorClassificationCell(a, org).text;
    fieldB = getVendorClassificationCell(b, org).text;
  } else if (
    sortOrder === "us_classification_ascending" ||
    sortOrder === "us_classification_descending"
  ) {
    fieldA = getVendorClassificationCell(a, org).text;
    fieldB = getVendorClassificationCell(b, org).text;
  } else if (
    sortOrder === "gdpr_classification_ascending" ||
    sortOrder === "gdpr_classification_descending"
  ) {
    fieldA = getGdprClassificationCell(a).text;
    fieldB = getGdprClassificationCell(b).text;
  }

  let isAscending = sortOrder.endsWith("_ascending");

  let comparison = fieldA.localeCompare(fieldB);

  if (comparison != 0) {
    return isAscending ? comparison : -comparison;
  } else {
    return isAscending ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
  }
};

function VendorDocumentationTableRow({
  org,
  vendor,
  updateVendor,
  isSidebarOpen,
  openSidebar,
  closeSidebar,
  navigateNextVendorInSidebar,
  navigatePreviousVendorInSidebar,
  sellingAndSharingAnswers,
  allVendors,
}: {
  org: OrganizationDto;
  vendor: OrganizationDataRecipientDto;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  navigateNextVendorInSidebar: () => void;
  navigatePreviousVendorInSidebar: () => void;
  sellingAndSharingAnswers: SurveyAnswers;
  allVendors: OrganizationDataRecipientDto[];
}) {
  const tableCellStyles = clsx("border-b border-solid border-gray-200");

  // Calculate column widths based on GDPR status
  const dataColWidth = org.featureGdpr ? "143px" : "201px";
  const lastDataColWidth = org.featureGdpr ? "147px" : "201px"; // Last data column gets extra pixels
  const tableCell = (
    children: React.ReactNode,
    customStyles: { colWidth: string; px?: string; py?: string },
  ) => (
    <td
      className={tableCellStyles}
      style={{
        width: customStyles.colWidth,
        minWidth: customStyles.colWidth,
        maxWidth: customStyles.colWidth,
        paddingLeft: customStyles.px || "24px",
        paddingRight: customStyles.px || "24px",
        paddingTop: customStyles.py || "16px",
        paddingBottom: customStyles.py || "16px",
        lineHeight: "18px",
      }}
    >
      {children}
    </td>
  );

  const [isDisabledWhileLoading, setIsDisabledWhileLoading] = useState(false);

  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);

  const [isSafeCountriesDialogOpen, setIsSafeCountriesDialogOpen] = useState(false);

  let disclosureState = initialStateFrom(vendor, sellingAndSharingAnswers, org);
  let disclosureMeta = calculateDisclosureMeta(
    vendor,
    disclosureState,
    /* custom = */ false,
    sellingAndSharingAnswers,
    allVendors,
    vendor?.category,
  );

  let vendorEmail = vendor.email || vendor.defaultEmail;
  let hasValidVendorEmail = vendorEmail && vendorEmail.length > 0 && validator.isEmail(vendorEmail);

  let isUsRegion = vendor.processingRegions.includes("UNITED_STATES");

  let isThirdParty = isThirdPartyRecipient(vendor);
  let isEeaUkRegion = org.featureGdpr && vendor.processingRegions.includes("EEA_UK");
  let isGdprProcessor = vendor.gdprProcessorSetting === "Processor";
  let gdprClassificationUrl = isGdprProcessor
    ? vendor.gdprProcessorGuaranteeUrl
    : vendor.gdprControllerGuaranteeUrl;
  let gdprClassificationFileName = isGdprProcessor
    ? vendor.gdprProcessorGuaranteeFileName
    : vendor.gdprControllerGuaranteeFileName;
  let gdprClassificationFileKey = isGdprProcessor
    ? vendor.gdprControllerGuaranteeFileKey
    : vendor.gdprControllerGuaranteeFileKey;

  let gdprProcessorOptions = _.clone(PROCESSOR_CLASSIFICATION_OPTIONS);
  const gdprProcessorOptionsMap = _.keyBy(gdprProcessorOptions, "key");

  let internationalDataTransferValue = vendor.gdprSccVendorReviewStatus;
  if (vendor.gdprSccVendorReviewStatus === null && vendor.gdprHasSccRecommendation) {
    internationalDataTransferValue = "DPF_LIST";
  }

  if (disclosureState.processorClassification === "Unknown") {
    gdprProcessorOptionsMap["unknown"].disabled = true;
    gdprProcessorOptionsMap["unknown"].visible = true;
  } else {
    gdprProcessorOptionsMap["unknown"].visible = false;
  }

  const isMailboxConnected = org?.mailbox?.mailboxStatus === "OK";

  return (
    <>
      <tr
        key={vendor.id}
        className={clsx("border-t border-solid border-gray-200", "cursor-pointer", {
          "bg-neutral-0": isSidebarOpen,
        })}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openSidebar();
        }}
      >
        {/* Vendor Name */}
        {tableCell(
          <div className="flex items-center gap-[12px]">
            <img src={vendor.logoUrl} alt={vendor.name} className="h-6 w-6" />
            <span className="text-[#111827] font-medium text-[14px]">{vendor.name}</span>
          </div>,
          { colWidth: dataColWidth },
        )}

        {/* Regions */}
        {tableCell(
          <div className="flex justify-center items-center gap-[3px]">
            {vendor.processingRegions
              .filter((r) => {
                // Even if the vendor has a non-USA region, need to also check region at the org-level
                if (r == "EEA_UK") {
                  return org.featureGdpr;
                }
                if (r == "CANADA") {
                  return org.featurePipeda;
                }
                return true;
              })
              .map((region: ProcessingRegion) => {
                return ProcessingRegionToFlagIcon[region];
              })}
          </div>,
          { colWidth: dataColWidth },
        )}

        {/* CA Contract */}
        {tableCell(
          <div className="grid place-items-center">
            <BadgedTextPill cellPillDisplay={getCaContractRequirementCell(vendor)} />
          </div>,
          { colWidth: dataColWidth },
        )}

        {/* GPP Signal */}
        {tableCell(
          <div className="grid place-items-center">
            <BadgedTextPill cellPillDisplay={getGppSignalCell(vendor)} />
          </div>,
          { colWidth: dataColWidth, px: "6px" },
        )}

        {/* US Classification */}
        {tableCell(
          <div className="grid place-items-center">
            <BadgedTextPill cellPillDisplay={getVendorClassificationCell(vendor, org)} />
          </div>,
          { colWidth: org.featureGdpr ? dataColWidth : lastDataColWidth },
        )}

        {org.featureGdpr && (
          <>
            {/* GDPR Classification */}
            {tableCell(
              <div className="grid place-items-center">
                <BadgedTextPill cellPillDisplay={getGdprClassificationCell(vendor)} />
              </div>,
              { colWidth: dataColWidth },
            )}

            {/* International Data Transfers */}
            {tableCell(
              isEeaUkRegion && doesGdprInternationalDataTransferApplyForVendor(vendor) ? (
                <div className="text-neutral-700 text-[14px] font-normal text-center mx-auto">
                  {internationalDataTransferValue ? (
                    getInternationalDataTransferCellText(internationalDataTransferValue)
                  ) : (
                    <BadgedText
                      text="Unverified"
                      textColor="text-yellow-800"
                      bgColor="bg-yellow-100"
                    />
                  )}
                </div>
              ) : (
                <div className="grid place-items-center">
                  <BadgedTextPill cellPillDisplay={{ text: "-" }} />
                </div>
              ),
              { colWidth: lastDataColWidth },
            )}
          </>
        )}

        <td className={clsx(tableCellStyles, "w-[50px] max-w-[50px] min-w-[50px]")}>
          <ArrowRightCircleIcon
            className="w-[20px] h-[20px] text-primary-600 cursor-pointer"
            onClick={openSidebar}
          />
        </td>
      </tr>

      <Slideout
        open={isSidebarOpen}
        setOpen={(newIsOpen: boolean) => {
          if (newIsOpen) {
            openSidebar();
          } else {
            closeSidebar();
          }
        }}
        slideFrom="right"
      >
        <>
          {isDisabledWhileLoading && (
            <div className="absolute top-0 left-0 w-full h-full bg-white bg-opacity-50 z-10">
              <div className="flex items-center justify-center w-full h-full">
                <CircularProgress size="6em" />
              </div>
            </div>
          )}

          <div className="h-full flex" style={{ flexDirection: "column" }}>
            {/* Sidebar header (vendor name + logo + URL) */}
            <div
              className={clsx(
                "h-[150px] flex-shrink-0",
                "bg-neutral-0 p-[24px] border border-b-1 border-gray-200",
              )}
            >
              <div className="flex justify-between items-center">
                <h3 className="text-gray-900 text-[18px] font-bold">Vendor Documentation</h3>
                <div className="cursor-pointer" onClick={closeSidebar}>
                  <XMarkIcon className="w-6 h-6 text-[#9CA3AF]" />
                </div>
              </div>

              <div className="mt-1">
                <div className="flex items-center">
                  <img src={vendor.logoUrl} alt={vendor.name} className="h-5 w-5" />
                  <span className="text-gray-900 font-medium text-[18px] ml-[12px] mr-[12px]">
                    {vendor.name}
                  </span>
                </div>
                {vendor.url?.length > 0 && (
                  <div className="mt-1 text-[14px] font-normal text-gray-500">{vendor.url}</div>
                )}
              </div>
            </div>

            {/* Sidebar editing UI */}
            <div className="flex-1 overflow-y-auto">
              <Form
                onSubmit={() => {}}
                render={() => {
                  return (
                    <>
                      {/* Vendor Contact Email */}
                      <div className="px-[24px] py-[12px]">
                        <label className="block text-[14px] font-medium text-gray-700">
                          Vendor Contact Email for Classification Queries
                        </label>
                        <TextInput
                          field="vendor-classification-contact-email"
                          onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
                            let newEmail = e.target.value;

                            await updateVendor({
                              vendorId: vendor.vendorId,
                              vendorSettings: {
                                ...vendor,
                                email: newEmail,
                              },
                            });
                          }}
                          defaultValue={vendor.email || vendor.defaultEmail}
                          maxLength={90}
                          disabled={isDisabledWhileLoading}
                        />
                      </div>

                      {!isVendorDocumentationClassified(org, vendor) && (
                        <div className="px-[24px] mb-[16px]">
                          <EmailVendorButton
                            onEmailVendorClick={() => setIsEmailDialogOpen(true)}
                            emailSentDate={vendor.serviceProviderInfoRequestSentDate}
                            disableSendEmailButton={!hasValidVendorEmail || !isMailboxConnected}
                            disableSendEmailButtonTooltip={
                              hasValidVendorEmail
                                ? isMailboxConnected
                                  ? null
                                  : "Mailbox not connected"
                                : "No contact email set"
                            }
                          />
                          <EmailVendorDialog
                            org={org}
                            vendor={vendor}
                            updateVendor={updateVendor}
                            isOpen={isEmailDialogOpen}
                            closeModal={() => setIsEmailDialogOpen(false)}
                          />
                        </div>
                      )}

                      <hr className="border-gray-200" />

                      {/* Regions */}
                      <div className="px-[24px] py-[16px]">
                        <div className="text-[14px] text-neutral-900 font-medium">Regions</div>
                        <div className="text-neutral-500 text-[14px] font-normal leading-[16px] mt-[6px] mb-[12px]">
                          From which region(s) is customer data is sent to {vendor.name}?
                        </div>
                        {org.featureGdpr && (
                          <VendorRegionCheckbox
                            vendor={vendor}
                            region="EEA_UK"
                            updateVendor={updateVendor}
                          />
                        )}
                        {org.featurePipeda && (
                          <VendorRegionCheckbox
                            vendor={vendor}
                            region="CANADA"
                            updateVendor={updateVendor}
                          />
                        )}
                        <VendorRegionCheckbox
                          vendor={vendor}
                          region="UNITED_STATES"
                          updateVendor={async (v: UpdateVendorArgs) => {
                            // Delete existing US service provider agreement when disabling US region
                            let isUsRegion =
                              v.vendorSettings.processingRegions.find(
                                (r) => r == "UNITED_STATES",
                              ) !== undefined;
                            if (!isUsRegion) {
                              await Api.storage.deleteOrganizationVendorAgreement(
                                org.id,
                                vendor.vendorId,
                                vendor.tosFileKey,
                              );

                              v.vendorSettings.serviceProviderInfoRequestSentDate =
                                "1970-01-01T00:00:00Z";
                            }

                            await updateVendor(v);
                          }}
                        />
                        {isUsRegion && (
                          <div className="ml-[30px] mt-[6px]">
                            <SimpleCheckbox
                              name={`regulated-by-other-us-privacy-laws`}
                              label={
                                <div className="ml-[12px] text-neutral-700 text-[14px] font-normal leading-[16px]">
                                  This vendor is regulated by another privacy law (GLBA, FCRA,
                                  HIPAA, FERPA)
                                </div>
                              }
                              initialChecked={vendor.regulatedByOtherUSPrivacyLaws}
                              onChange={async (newChecked) => {
                                await updateVendor({
                                  vendorId: vendor.vendorId,
                                  vendorSettings: {
                                    ...vendor,
                                    regulatedByOtherUSPrivacyLaws: newChecked,
                                  },
                                });
                              }}
                            />
                          </div>
                        )}
                      </div>

                      <hr className="border-gray-200" />

                      {/* CA Contract Requirement */}
                      {isCaContractFieldRequiredForVendor(vendor) && (
                        <div className="px-[24px] py-[16px]">
                          <div className="flex items-center gap-[6px]">
                            <div className="text-[14px] text-neutral-900 font-medium">
                              California Contract Requirement
                            </div>
                            <ToolTip
                              text={
                                "Special data protections required by California law to be included in your contracts with vendors."
                              }
                              width="210px"
                              xOffset={-60}
                              yOffset={125}
                            >
                              <InformationCircleIcon className="w-[16px] h-[16px] text-neutral-500" />
                            </ToolTip>
                            {!isVendorCaContractDocumentationComplete(vendor) && (
                              <BadgedTextPill
                                cellPillDisplay={{ color: "yellow", text: "Unverified" }}
                              />
                            )}
                          </div>
                          <div className="text-neutral-500 leading-[16px] text-[14px] font-normal mt-[6px] mb-[12px]">
                            Upload or link to a document below, otherwise contact this vendor for
                            documentation. Click{" "}
                            <a
                              href="https://knowledge.truevault.com/article/353-sample-california-contract-template"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-700 underline"
                            >
                              here
                            </a>{" "}
                            to access a contract template you can use to satisfy this requirement.
                          </div>
                          <VendorRadioSection
                            recipient={vendor}
                            options={[
                              {
                                key: "ca-contract-requirement",
                                label: "CA Contract Requirement",
                                type: "file",
                                value: "ca-contract-requirement",
                              },
                            ]}
                            value={"ca-contract-requirement"}
                            url={
                              vendor.caContractRequirementUrl ??
                              vendor.cpraContractLanguageUrl ??
                              ""
                            }
                            fileName={
                              vendor.caContractRequirementFileName === "DELETE_"
                                ? null
                                : vendor.caContractRequirementFileName || ""
                            }
                            fileKey={
                              vendor.caContractRequirementFileName === "DELETE_"
                                ? null
                                : vendor.caContractRequirementFileName || ""
                            }
                            onSelectionChange={async () => {}}
                            onUrlChange={async (newUrl: string) => {
                              setIsDisabledWhileLoading(true);

                              await updateVendor({
                                vendorId: vendor.vendorId,
                                vendorSettings: {
                                  ...vendor,
                                  caContractRequirementUrl: newUrl,
                                },
                              });

                              setIsDisabledWhileLoading(false);
                            }}
                            onFileUpload={async (fileName, fileData) => {
                              setIsDisabledWhileLoading(true);

                              await saveCaContractRequirementDocumentation(
                                org,
                                vendor,
                                fileName,
                                fileData,
                                updateVendor,
                              );

                              setIsDisabledWhileLoading(false);
                            }}
                            onFileDelete={async () => {
                              setIsDisabledWhileLoading(true);

                              await saveCaContractRequirementDocumentation(
                                org,
                                vendor,
                                "DELETE_",
                                null,
                                updateVendor,
                              );

                              setIsDisabledWhileLoading(false);
                            }}
                            hideSelectionRadioIfOnlyOneOption={true}
                            useAquiloDesign={true}
                          />
                        </div>
                      )}

                      <hr className="border-gray-200" />

                      {/* Browser Opt-Out / GPP Signal */}
                      {doesGppSignalApplyForVendor(vendor) && (
                        <div className="px-[24px] py-[16px]">
                          <div className="flex items-center gap-[6px]">
                            <div className="text-[14px] text-neutral-900 font-medium">
                              Browser Opt-Out
                            </div>
                            <ToolTip
                              text={
                                "Global Privacy Protocol (GPP) signals are the industry standard for communicating advertising opt-outs and consent online. Upload or link to a document below demonstrating the vendor is an IAB GPP signatory."
                              }
                              width="210px"
                              xOffset={-60}
                              yOffset={215}
                            >
                              <InformationCircleIcon className="w-[16px] h-[16px] text-neutral-500" />
                            </ToolTip>
                            {!isVendorGppSignalDocumentationComplete(vendor) && (
                              <BadgedTextPill
                                cellPillDisplay={{ color: "yellow", text: "Unverified" }}
                              />
                            )}
                          </div>
                          <div className="text-neutral-500 leading-[16px] text-[14px] font-normal mt-[6px] mb-[12px]">
                            If a vendor has not agreed to respect browser opt-outs, your website
                            setup instructions will be updated to block data sharing with the
                            vendor.
                          </div>
                          <div className="text-neutral-500 text-[14px] font-normal mt-[6px] mb-[12px] leading-[16px]"></div>
                          <VendorRadioSection
                            recipient={vendor}
                            options={[
                              {
                                key: "gpp-signal-respected",
                                label: "Respects GPP Signal",
                                type: "file",
                                value: "GPP_SIGNAL_RESPECTED", // value from `GppSignalStatus`
                              },
                              {
                                key: "gpp-alternate-opt-out-mechanism",
                                label: "Alternate opt-out mechanism",
                                type: "normal",
                                value: "GPP_ALTERNATE_OPT_OUT_MECHANISM", // value from `GppSignalStatus`
                              },
                              {
                                key: "gpp-signal-not-respected-or-unknown",
                                label: "Unknown/does not respect GPP",
                                type: "normal",
                                value: "GPP_SIGNAL_NOT_RESPECTED_OR_UNKNOWN", // value from `GppSignalStatus`
                              },
                            ]}
                            value={vendor.gppSignalStatus}
                            url={vendor.gppSignalDocumentationUrl ?? ""}
                            fileName={
                              vendor.gppSignalDocumentationFileName === "DELETE_"
                                ? null
                                : vendor.gppSignalDocumentationFileName || ""
                            }
                            fileKey={
                              vendor.gppSignalDocumentationFileName === "DELETE_"
                                ? null
                                : vendor.gppSignalDocumentationFileKey || ""
                            }
                            onSelectionChange={async (newValue: GppSignalStatus) => {
                              setIsDisabledWhileLoading(true);

                              await updateVendor({
                                vendorId: vendor.vendorId,
                                vendorSettings: {
                                  ...vendor,
                                  gppSignalStatus: newValue,
                                },
                              });

                              setIsDisabledWhileLoading(false);
                            }}
                            onUrlChange={async (newUrl: string) => {
                              setIsDisabledWhileLoading(true);

                              await updateVendor({
                                vendorId: vendor.vendorId,
                                vendorSettings: {
                                  ...vendor,
                                  gppSignalDocumentationUrl: newUrl,
                                },
                              });

                              setIsDisabledWhileLoading(false);
                            }}
                            onFileUpload={async (fileName, fileData) => {
                              setIsDisabledWhileLoading(true);

                              await saveGppSignalDocumentation(
                                org,
                                vendor,
                                fileName,
                                fileData,
                                updateVendor,
                              );

                              setIsDisabledWhileLoading(false);
                            }}
                            onFileDelete={async () => {
                              setIsDisabledWhileLoading(true);

                              await saveGppSignalDocumentation(
                                org,
                                vendor,
                                "DELETE_",
                                null,
                                updateVendor,
                              );

                              setIsDisabledWhileLoading(false);
                            }}
                            useAquiloDesign={true}
                          />
                        </div>
                      )}

                      <hr className="border-gray-200" />

                      {/* Platform Requests */}
                      {vendor.isPlatform !== true && (
                        <div className="px-[24px] py-[16px]">
                          <div className="flex items-center gap-[6px]">
                            <div className="text-[14px] text-neutral-900 font-medium">
                              Platform Requests
                            </div>
                            <ToolTip
                              text={
                                "Some platforms like Shopify automatically forward updates to customer lists (e.g., the deletion of a customer or a data sale opt-out) to installed apps. If an app respects these signals, it will be grouped under the platform in consent management workflows. For example, if a vendor respects Shopify's customer events, consumers only need to submit one request to Shopify rather than separate requests to each app."
                              }
                              width="280px"
                              xOffset={-100}
                              yOffset={200}
                            >
                              <InformationCircleIcon className="w-[16px] h-[16px] text-neutral-500" />
                            </ToolTip>
                            {vendor.vendorRespectsPlatformRequests && (
                              <BadgedTextPill
                                cellPillDisplay={{ color: "green", text: "Verified" }}
                              />
                            )}
                          </div>
                          <div className="text-neutral-500 leading-[16px] text-[14px] font-normal mt-[6px] mb-[12px]">
                            {vendor.vendorRespectsPlatformRequests
                              ? "This vendor has been verified by TrueVault to respect platform-level opt-out signals."
                              : "Indicate whether this vendor respects platform-level opt-out signals and deletion requests from platforms like Shopify."}
                          </div>
                          {vendor.vendorRespectsPlatformRequests ? (
                            <div className="text-neutral-700 text-[14px] font-medium bg-neutral-100 rounded-[6px] px-[12px] py-[8px]">
                              Respects Platform Requests
                            </div>
                          ) : (
                            <VendorRadioSection
                              recipient={vendor}
                              options={[
                                {
                                  key: "platform-requests-yes",
                                  label: "Respects platform requests",
                                  type: "normal",
                                  value: "true",
                                },
                                {
                                  key: "platform-requests-no",
                                  label: "Does not respect platform requests (Recommended)",
                                  type: "normal",
                                  value: "false",
                                },
                              ]}
                              value={
                                vendor.respectsPlatformRequests === true
                                  ? "true"
                                  : vendor.respectsPlatformRequests === false
                                  ? "false"
                                  : null
                              }
                              url=""
                              fileName=""
                              fileKey=""
                              onSelectionChange={async (newValue: string) => {
                                setIsDisabledWhileLoading(true);

                                await updateVendor({
                                  vendorId: vendor.vendorId,
                                  vendorSettings: {
                                    ...vendor,
                                    respectsPlatformRequests: newValue === "true",
                                  },
                                });

                                setIsDisabledWhileLoading(false);
                              }}
                              onUrlChange={async () => {}}
                              onFileUpload={async () => {}}
                              onFileDelete={async () => {}}
                              hideDocumentationInput={true}
                              useAquiloDesign={true}
                            />
                          )}
                        </div>
                      )}

                      <hr className="border-gray-200" />

                      {/* US Classification */}
                      {isUsClassificationRequiredForVendor(vendor, org) &&
                        disclosureMeta.showClassification && (
                          <div className="px-[24px] py-[16px]">
                            <div className="flex items-center gap-[6px]">
                              <div className="text-[14px] text-neutral-900 font-medium">
                                {isThirdParty
                                  ? "US Classification: Third Party"
                                  : "US Classification"}
                              </div>
                              <ToolTip
                                text={`Upload or link to a document below, otherwise contact this vendor for documentation. Or, click the "Email Vendor" button above to send a templated email asking for documentation.`}
                                width="210px"
                                xOffset={-60}
                                yOffset={200}
                              >
                                <InformationCircleIcon className="w-[16px] h-[16px] text-neutral-500" />
                              </ToolTip>
                              {!doesVendorHaveUsClassification(vendor) && (
                                <BadgedTextPill
                                  cellPillDisplay={{ color: "yellow", text: "Unverified" }}
                                />
                              )}
                            </div>
                            <div className="text-neutral-500 text-[14px] font-normal mt-[6px] mb-[12px]">
                              <ServiceProviderLanguage
                                vendor={vendor}
                                isThirdPartyRecipient={isThirdParty}
                                disclosureMeta={disclosureMeta}
                              />
                            </div>
                            <VendorRadioSection
                              recipient={vendor}
                              options={
                                isThirdPartyRecipient(vendor) ||
                                (disclosureMeta.lockClassification &&
                                  disclosureState.classification === "third-party")
                                  ? [THIRD_PARTY_CLASSIFICATION_OPTION]
                                  : CLASSIFICATION_OPTIONS
                              }
                              value={disclosureState.classification}
                              url={disclosureState.publicTosUrl ?? ""}
                              fileName={
                                vendor?.tosFileName === "DELETE_" ? null : vendor?.tosFileName || ""
                              }
                              fileKey={
                                vendor?.tosFileName === "DELETE_" ? null : vendor?.tosFileName || ""
                              }
                              onSelectionChange={async (o: ClassificationType) => {
                                setIsDisabledWhileLoading(true);

                                await updateVendor({
                                  vendorId: vendor.vendorId,
                                  vendorSettings: {
                                    ...vendor,
                                    classificationId: getClassificationIdForClassificationType(
                                      o,
                                      vendor.classificationOptions,
                                    ),
                                  },
                                });

                                setIsDisabledWhileLoading(false);
                              }}
                              onUrlChange={async (publicTosUrl) => {
                                setIsDisabledWhileLoading(true);

                                let updatedVendor = {
                                  ...vendor,
                                  publicTosUrl,
                                };

                                // Reset email sent date when URL is added
                                if (publicTosUrl) {
                                  updatedVendor.serviceProviderInfoRequestSentDate =
                                    "1970-01-01T00:00:00Z";
                                }

                                await updateVendor({
                                  vendorId: vendor.vendorId,
                                  vendorSettings: updatedVendor,
                                });

                                setIsDisabledWhileLoading(false);
                              }}
                              hideDocumentationInput={
                                !disclosureMeta.showServiceProviderDocumentationInput
                              }
                              onFileUpload={async (fileName, fileData) => {
                                setIsDisabledWhileLoading(true);

                                await saveServiceProviderDocumentation(
                                  org,
                                  vendor,
                                  fileName,
                                  fileData,
                                  disclosureState,
                                  updateVendor,
                                );

                                setIsDisabledWhileLoading(false);
                              }}
                              onFileDelete={async () => {
                                setIsDisabledWhileLoading(true);

                                await saveServiceProviderDocumentation(
                                  org,
                                  vendor,
                                  "DELETE_",
                                  null,
                                  disclosureState,
                                  updateVendor,
                                );

                                setIsDisabledWhileLoading(false);
                              }}
                              forceShowOptional={isThirdPartyDocumentationOptional(disclosureState)}
                              useAquiloDesign={true}
                            />
                            {/* Contract stored internally checkbox for US Classification */}
                            {disclosureMeta.showServiceProviderDocumentationInput && (
                              <div className="ml-[24px] mt-[12px]">
                                <VendorBooleanQuestion
                                  question="Contract stored internally"
                                  questionKey="contract-stored-internally"
                                  condensed={false}
                                  helpText="Select `Yes` if the contract documentation is stored internally"
                                  value={vendor.contractStoredInternally}
                                  onChange={async (newVal) => {
                                    setIsDisabledWhileLoading(true);

                                    await updateVendor({
                                      vendorId: vendor.vendorId,
                                      vendorSettings: {
                                        ...vendor,
                                        contractStoredInternally: newVal,
                                      },
                                    });

                                    setIsDisabledWhileLoading(false);
                                  }}
                                  useAquiloDesign={true}
                                />
                              </div>
                            )}
                          </div>
                        )}

                      <hr className="border-gray-200" />

                      {/* GDPR Classification */}
                      {isEeaUkRegion && disclosureMeta.showGdprProcessorClassification && (
                        <div className="px-[24px] py-[16px]">
                          <div className="flex items-center gap-[6px]">
                            <div className="text-[14px] text-neutral-900 font-medium">
                              GDPR Classification
                            </div>
                            <ToolTip
                              text={`Upload or link to a document below, otherwise contact this vendor for documentation. Or, click the "Email Vendor" button above to send a templated email asking for documentation.`}
                              width="210px"
                              xOffset={-60}
                              yOffset={195}
                            >
                              <InformationCircleIcon className="w-[16px] h-[16px] text-neutral-500" />
                            </ToolTip>
                            {!isVendorGdprClassificationComplete(vendor) && (
                              <BadgedTextPill
                                cellPillDisplay={{ color: "yellow", text: "Unverified" }}
                              />
                            )}
                          </div>
                          <div className="text-neutral-500 text-[14px] font-normal mt-[6px] mb-[12px]">
                            <GdprProcessorLanguage vendor={vendor} meta={disclosureMeta} />
                          </div>
                          <VendorRadioSection
                            recipient={vendor}
                            options={gdprProcessorOptions}
                            value={vendor.gdprProcessorSetting}
                            url={
                              gdprClassificationUrl
                                ? gdprClassificationUrl
                                : vendor.gdprProcessorLanguageUrl
                            }
                            fileName={
                              gdprClassificationFileName === "DELETE_"
                                ? null
                                : gdprClassificationFileName || ""
                            }
                            fileKey={
                              gdprClassificationFileName === "DELETE_"
                                ? null
                                : gdprClassificationFileKey || ""
                            }
                            onSelectionChange={async (o: GDPRProcessorRecommendation) => {
                              setIsDisabledWhileLoading(true);

                              await updateVendor({
                                vendorId: vendor.vendorId,
                                vendorSettings: {
                                  ...vendor,
                                  gdprProcessorSetting: o,
                                },
                              });

                              setIsDisabledWhileLoading(false);
                            }}
                            onUrlChange={async (newUrl: string) => {
                              setIsDisabledWhileLoading(true);

                              let updatedVendor = { ...vendor };

                              if (vendor.gdprProcessorSetting == "Processor") {
                                updatedVendor.gdprProcessorGuaranteeUrl = newUrl;
                              } else if (vendor.gdprProcessorSetting == "Controller") {
                                updatedVendor.gdprControllerGuaranteeUrl = newUrl;
                              }

                              // Reset email sent date when URL is added
                              if (newUrl) {
                                updatedVendor.serviceProviderInfoRequestSentDate =
                                  "1970-01-01T00:00:00Z";
                              }

                              updateVendor({
                                vendorId: vendor.vendorId,
                                vendorSettings: updatedVendor,
                              });

                              setIsDisabledWhileLoading(false);
                            }}
                            onFileUpload={async (fileName, fileData) => {
                              setIsDisabledWhileLoading(true);

                              await saveGdprDocumentation(
                                org,
                                vendor,
                                fileName,
                                fileData,
                                disclosureState,
                                updateVendor,
                              );

                              setIsDisabledWhileLoading(false);
                            }}
                            onFileDelete={async () => {
                              setIsDisabledWhileLoading(true);

                              await saveGdprDocumentation(
                                org,
                                vendor,
                                "DELETE_",
                                null,
                                disclosureState,
                                updateVendor,
                              );

                              setIsDisabledWhileLoading(false);
                            }}
                            disabled={disclosureMeta.lockGdprProcessorClassification}
                            forceShowOptional={isGDPRControllerDocumentationOptional(
                              disclosureState,
                            )}
                            useAquiloDesign={true}
                          />
                          {/* Contract stored internally checkbox for GDPR Classification */}
                          {disclosureMeta.showServiceProviderDocumentationInput && (
                            <div className="ml-[24px] mt-[12px]">
                              <VendorBooleanQuestion
                                question="GDPR contract stored internally"
                                questionKey="gdpr-contract-stored-internally"
                                condensed={false}
                                helpText="Select `Yes` if the contract documentation is stored internally"
                                value={vendor.gdprContractStoredInternally}
                                onChange={async (newVal) => {
                                  setIsDisabledWhileLoading(true);

                                  await updateVendor({
                                    vendorId: vendor.vendorId,
                                    vendorSettings: {
                                      ...vendor,
                                      gdprContractStoredInternally: newVal,
                                    },
                                  });

                                  setIsDisabledWhileLoading(false);
                                }}
                                useAquiloDesign={true}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      <hr className="border-gray-200" />

                      {/* International Data Transfers */}
                      {isEeaUkRegion && doesGdprInternationalDataTransferApplyForVendor(vendor) && (
                        <div className="px-[24px] py-[16px]">
                          <div className="flex items-center gap-[6px]">
                            <div className="text-[14px] text-neutral-900 font-medium">
                              International Data Transfers
                            </div>
                            {!isGdprInternationalDataTransferComplete(vendor) && (
                              <BadgedTextPill
                                cellPillDisplay={{ color: "yellow", text: "Unverified" }}
                              />
                            )}
                          </div>
                          <div className="text-neutral-500 text-[14px] font-normal leading-[16px] mt-[6px] mb-[12px]">
                            Data transfers to {vendor.name} are considered "safe" under GDPR only if{" "}
                            {vendor.name} participates in the EU-US Data Privacy Framework (DPF), if
                            it incorporates Standard Contractual Clauses (SCCs) into its agreements
                            with your business, or if consumer's personal data is transferred solely
                            to fulfill a contract with the consumer. If you are relying on{" "}
                            {vendor.name}'s DPF participation or its SCCS for the transfer, add a
                            link to its DPF profile page or its online SCCS, or alternatively you
                            can upload an agreement which contains the SCCs.{" "}
                            <a
                              href="https://knowledge.truevault.com/article/202-standard-contractual-clauses"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 underline"
                            >
                              Read more about SCCs and how to look for them
                            </a>
                            .
                          </div>
                          <div>
                            <VendorRadioSection
                              recipient={vendor}
                              options={internationalDataTransferOptionsForVendor(
                                vendor,
                                org,
                                () => {
                                  setIsSafeCountriesDialogOpen(true);
                                },
                              )}
                              value={internationalDataTransferValue}
                              url={vendor.gdprSccUrl ?? ""}
                              fileName={
                                vendor.gdprSccFileName === "DELETE_"
                                  ? null
                                  : vendor.gdprSccFileName || ""
                              }
                              fileKey={
                                vendor.gdprSccFileName === "DELETE_"
                                  ? null
                                  : vendor.gdprSccFileName || ""
                              }
                              onSelectionChange={async (newValue: GdprSccVendorReviewStatus) => {
                                await updateVendor({
                                  vendorId: vendor.vendorId,
                                  vendorSettings: {
                                    ...vendor,
                                    gdprSccVendorReviewStatus: newValue,
                                  },
                                });
                              }}
                              onFileUpload={async (fileName, fileData) => {
                                setIsDisabledWhileLoading(true);

                                await saveSccDocumentation(
                                  org,
                                  vendor,
                                  fileName,
                                  fileData,
                                  disclosureState,
                                  updateVendor,
                                );

                                setIsDisabledWhileLoading(false);
                              }}
                              onFileDelete={async () => {
                                setIsDisabledWhileLoading(true);

                                await saveSccDocumentation(
                                  org,
                                  vendor,
                                  "DELETE_",
                                  null,
                                  disclosureState,
                                  updateVendor,
                                );

                                setIsDisabledWhileLoading(false);
                              }}
                              onUrlChange={async (newUrl) => {
                                let updatedVendor = {
                                  ...vendor,
                                  gdprSccUrl: newUrl,
                                };

                                // Reset email sent date when URL is added
                                if (newUrl) {
                                  updatedVendor.serviceProviderInfoRequestSentDate =
                                    "1970-01-01T00:00:00Z";
                                }

                                await updateVendor({
                                  vendorId: vendor.vendorId,
                                  vendorSettings: updatedVendor,
                                });
                              }}
                              hideDocumentationInput={false}
                              forceShowOptional={isThirdPartyDocumentationOptional(disclosureState)}
                              useAquiloDesign={true}
                            />
                            <SafeCountriesDialog
                              open={isSafeCountriesDialogOpen}
                              onClose={() => setIsSafeCountriesDialogOpen(false)}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  );
                }}
              />
            </div>

            {/* Sidebar footer */}
            <div
              className={clsx(
                "h-[62px] flex-shrink-0",
                "p-[16px] border border-t-1 border-gray-200",
                "flex items-center justify-between",
              )}
            >
              <Button
                color="tertiary"
                size="s"
                customSizeClassname="h-[30px] w-auto"
                onClick={navigatePreviousVendorInSidebar}
              >
                <div className="flex items-center">
                  <ArrowLeftIcon className="w-[16px] h-[16px] text-neutral-500" />
                  <span className="text-[12px] font-medium text-neutral-800 ml-[8px]">
                    Previous Vendor
                  </span>
                </div>
              </Button>
              <Button
                color="tertiary"
                size="s"
                customSizeClassname="h-[30px] w-auto"
                onClick={navigateNextVendorInSidebar}
              >
                <div className="flex items-center">
                  <ArrowRightIcon className="w-[16px] h-[16px] text-neutral-500" />
                  <span className="text-[12px] font-medium text-neutral-800 ml-[8px]">
                    Next Vendor
                  </span>
                </div>
              </Button>
            </div>
          </div>
        </>
      </Slideout>
    </>
  );
}

function EmailVendorButton({
  onEmailVendorClick,
  emailSentDate,
  disableSendEmailButton,
  disableSendEmailButtonTooltip,
}: {
  onEmailVendorClick?: () => void;
  emailSentDate: Iso8601Date | null;
  disableSendEmailButton?: boolean;
  disableSendEmailButtonTooltip?: string | null;
}) {
  let emailButton = (
    <Button color="primary" size="s" onClick={onEmailVendorClick} disabled={disableSendEmailButton}>
      <div className="flex items-center">
        <div>
          <EnvelopeIcon style={{ width: "16px", height: "16px", color: "white" }} />
        </div>
        <div className="text-[12px] font-medium text-white ml-[8px]">Email Vendor</div>
      </div>
    </Button>
  );

  if (
    disableSendEmailButton &&
    disableSendEmailButtonTooltip &&
    disableSendEmailButtonTooltip.length > 0
  ) {
    emailButton = (
      <ToolTip text={disableSendEmailButtonTooltip} yOffset={55}>
        {emailButton}
      </ToolTip>
    );
  }

  return (
    <div className="tw-boundary">
      <div className="mt-[6px]">{emailButton}</div>
      {emailSentDate && emailSentDate !== "1970-01-01T00:00:00Z" && (
        <div className="mt-[12px] bg-green-100 rounded-[6px] px-[12px] py-[7px] flex items-center">
          <div>
            <CheckCircleIcon className="w-[16px] h-[16px] text-green-400" />
          </div>
          <div className="ml-[12px] text-green-800 font-medium text-[12px]">
            You emailed this vendor on {Fmt.Date.fmtDate(emailSentDate)}
          </div>
        </div>
      )}
    </div>
  );
}

function BadgedTextPill({ cellPillDisplay }: { cellPillDisplay: VendorTableCellPillDisplay }) {
  let textColor = {
    gray: "text-gray-800",
    yellow: "text-yellow-800",
    green: "text-green-800",
  }[cellPillDisplay.color];

  let bgColor = {
    gray: "bg-gray-100",
    yellow: "bg-yellow-100",
    green: "bg-green-100",
  }[cellPillDisplay.color];

  if (cellPillDisplay.text === "-") {
    return <hr className="w-[51px] text-neutral-700 mx-auto" />;
  }

  if (cellPillDisplay.text === "GREEN_CHECKMARK_ICON") {
    return (
      <div className="flex items-center justify-center bg-green-100 rounded-[12px] w-[20px] h-[20px] p-[4px]">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="12"
          viewBox="0 0 13 12"
          fill="none"
        >
          <path
            d="M2.59985 6.5L4.59985 8.5L9.59985 3.5"
            stroke="#059669"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  }

  if (cellPillDisplay.text === "GRAY_X_ICON") {
    return (
      <div className="flex items-center justify-center bg-neutral-100 rounded-[12px] w-[20px] h-[20px] p-[4px]">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="12"
          viewBox="0 0 13 12"
          fill="none"
        >
          <path
            d="M4.46025 3.16475C4.24058 2.94508 3.88442 2.94508 3.66475 3.16475C3.44508 3.38442 3.44508 3.74058 3.66475 3.96025L5.70451 6L3.66475 8.03975C3.44508 8.25942 3.44508 8.61558 3.66475 8.83525C3.88442 9.05492 4.24058 9.05492 4.46025 8.83525L6.5 6.79549L8.53975 8.83525C8.75942 9.05492 9.11558 9.05492 9.33525 8.83525C9.55492 8.61558 9.55492 8.25942 9.33525 8.03975L7.29549 6L9.33525 3.96025C9.55492 3.74058 9.55492 3.38442 9.33525 3.16475C9.11558 2.94508 8.75942 2.94508 8.53975 3.16475L6.5 5.20451L4.46025 3.16475Z"
            fill="#726D83"
          />
        </svg>
      </div>
    );
  }

  return <BadgedText text={cellPillDisplay.text} textColor={textColor} bgColor={bgColor} />;
}

function BadgedText({
  text,
  textColor,
  bgColor,
}: {
  text: string;
  textColor: string;
  bgColor: string;
}) {
  return (
    <div
      className={clsx(
        "px-[10px] py-[2px] max-w-[188px] max-h-[18px] rounded-full",
        "inline-flex justify-center items-center",
        "truncate font-medium text-[12px]",
        textColor,
        bgColor,
      )}
    >
      {text}
    </div>
  );
}
